import { useFormContext } from "react-hook-form";
import { t } from "@/i18n-js/instance";
import { Icon } from "@circle-react-shared/Icon";
import { TippyV2 } from "@circle-react-shared/TippyV2";
import { TiptapEditor } from "@circle-react-shared/uikit/TipTap";
import { Typography } from "@circle-react-shared/uikit/Typography";
import { Form } from "@circle-react-uikit/Form";

interface BotBuilderFormValues {
  allow_direct_messages: boolean;
  allow_search_conversations: boolean;
  send_welcome_message: boolean;
  welcome_message: { body: string };
}

const getDisabledSectionClasses = (isDisabled: boolean) =>
  isDisabled ? "bg-secondary text-[var(--color-text-disabled)]" : "";

const getDisabledTextColor = (isDisabled: boolean) =>
  isDisabled ? "text-[var(--color-text-disabled)]" : undefined;

const getDisabledIconClasses = (isDisabled: boolean) =>
  isDisabled ? "text-[var(--color-icon-disabled)]" : "text-light";

const getStrokeColor = (isDisabled: boolean) =>
  isDisabled ? "var(--color-icon-disabled)" : "#A5A9AD";

export const TouchpointsSection = () => {
  const { getValues, watch, setValue } = useFormContext<BotBuilderFormValues>();
  const shouldSendWelcomeMessage = getValues("send_welcome_message");
  const welcomeMessage = getValues("welcome_message");
  const isAllowDirectMessages = watch("allow_direct_messages");
  const isAllowSearchConversations = watch("allow_search_conversations");
  const isSendWelcomeMessage = watch("send_welcome_message");

  const isDirectMessagesDisabled = !isAllowDirectMessages;
  const isSearchConversationsDisabled =
    isDirectMessagesDisabled || !isAllowSearchConversations;
  const isWelcomeMessageDisabled =
    isDirectMessagesDisabled || !isSendWelcomeMessage;

  const handleDirectMessagesToggle = (checked: boolean) => {
    // eslint-disable-next-line sonarjs/no-selector-parameter -- Disabled for plugin version update. Fix when possible.
    if (!checked) {
      // When direct messages is turned off, turn off the other two toggles
      setValue("allow_search_conversations", false);
      setValue("send_welcome_message", false);
    } else {
      // When direct messages is turned on, automatically turn on search conversations
      setValue("allow_search_conversations", true);
    }
  };

  return (
    <div className="border-primary px-6 pt-5">
      <div className="mb-4">
        <Typography.LabelMd weight="semibold">
          {t("community_bot.bot_builder.audience_page.touchpoints_title")}
        </Typography.LabelMd>
      </div>
      <div className="mb-3">
        <Typography.LabelSm color="text-light">
          {t("community_bot.bot_builder.audience_page.touchpoints_description")}
        </Typography.LabelSm>
      </div>

      <div
        className={`border-primary flex h-12 border-collapse items-center rounded-xl border px-4 py-3 ${getDisabledSectionClasses(isDirectMessagesDisabled)}`}
      >
        <div className="flex items-center gap-3">
          <Form.ToggleSwitch
            variant="small"
            shouldUseCheckmark={false}
            name="allow_direct_messages"
            onToggleChange={handleDirectMessagesToggle}
          />
          <Typography.LabelSm
            weight="semibold"
            color={getDisabledTextColor(isDirectMessagesDisabled)}
          >
            {t("community_bot.bot_builder.audience_page.allow_direct_messages")}
          </Typography.LabelSm>
          <TippyV2
            content={t(
              "community_bot.bot_builder.audience_page.allow_direct_messages_tooltip",
            )}
            className="flex"
          >
            <Icon
              type="16-question-mark"
              className={`${getDisabledIconClasses(isDirectMessagesDisabled)}`}
              size={16}
              useWithStrokeCurrentColor
            />
          </TippyV2>
        </div>
      </div>

      {isAllowDirectMessages && (
        <div className="mt-3 flex items-start">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="13"
            height="48.5"
            viewBox="0 0 13 48.5"
            fill="none"
            className="shrink-0"
            style={{
              marginRight: "8px",
              marginLeft: "30px",
            }}
          >
            <path
              opacity="0.5"
              d="M1 1V13.75M14 26.5H7C3.68629 26.5 1 23.8137 1 20.5V13.75M1 13.75V49.5"
              stroke={getStrokeColor(isDirectMessagesDisabled)}
              strokeLinecap="round"
            />
          </svg>
          <div
            className={`border-primary mb-3 flex h-12 flex-1 border-collapse items-center rounded-xl border px-4 py-3 ${getDisabledSectionClasses(isSearchConversationsDisabled)}`}
          >
            <div className="flex items-center gap-3">
              <Form.ToggleSwitch
                variant="small"
                shouldUseCheckmark={false}
                name="allow_search_conversations"
                disabled={isDirectMessagesDisabled}
              />
              <Typography.LabelSm
                weight="semibold"
                color={getDisabledTextColor(isSearchConversationsDisabled)}
              >
                {t(
                  "community_bot.bot_builder.audience_page.allow_search_conversations",
                )}
              </Typography.LabelSm>
              <TippyV2
                content={t(
                  "community_bot.bot_builder.audience_page.allow_search_conversations_tooltip",
                )}
                className="flex"
              >
                <Icon
                  type="16-question-mark"
                  className={`${getDisabledIconClasses(
                    isSearchConversationsDisabled,
                  )}`}
                  size={16}
                  useWithStrokeCurrentColor
                />
              </TippyV2>
            </div>
          </div>
        </div>
      )}

      {isAllowDirectMessages && (
        <div className="flex items-start">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="13"
            height="48.5"
            viewBox="0 0 13 48.5"
            fill="none"
            className="shrink-0"
            style={{
              marginRight: "8px",
              marginLeft: "30px",
            }}
          >
            <path
              opacity="0.5"
              d="M1 0.5V20C1 23.3137 3.68629 26 7 26H14"
              stroke={getStrokeColor(isDirectMessagesDisabled)}
              strokeLinecap="round"
            />
          </svg>
          <div className="flex flex-1 flex-col">
            <div
              className={`border-primary flex h-12 border-collapse items-center rounded-xl border px-4 py-3 ${getDisabledSectionClasses(isWelcomeMessageDisabled)} ${shouldSendWelcomeMessage ? "rounded-b-none" : ""}`}
            >
              <div className="flex items-center gap-3">
                <Form.ToggleSwitch
                  variant="small"
                  shouldUseCheckmark={false}
                  name="send_welcome_message"
                  disabled={isDirectMessagesDisabled}
                />
                <Typography.LabelSm
                  weight="semibold"
                  color={getDisabledTextColor(isWelcomeMessageDisabled)}
                >
                  {t(
                    "community_bot.bot_builder.audience_page.send_welcome_message",
                  )}
                </Typography.LabelSm>
                <TippyV2
                  content={t(
                    "community_bot.bot_builder.audience_page.welcome_message_tooltip",
                  )}
                  className="flex"
                >
                  <Icon
                    type="16-question-mark"
                    className={`${getDisabledIconClasses(isWelcomeMessageDisabled)}`}
                    size={16}
                    useWithStrokeCurrentColor
                  />
                </TippyV2>
              </div>
            </div>
            {shouldSendWelcomeMessage && (
              <div
                className={`border-primary rounded-b-xl border border-t-0 px-4 pb-4 pt-3 ${getDisabledSectionClasses(isWelcomeMessageDisabled)}`}
              >
                <TiptapEditor
                  editorClassName="bg-secondary p-3 text-sm text-light border border-primary rounded-xl"
                  disabled
                  editable={false}
                  value={welcomeMessage?.body}
                  showMenuBar={false}
                />
              </div>
            )}
          </div>
        </div>
      )}

      <div className="mt-6 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon type="16-info" size={16} className="text-light" />
          <Typography.LabelSm>
            {t("community_bot.bot_builder.audience_page.workflows_note")}
          </Typography.LabelSm>
        </div>
        <svg
          width="56"
          height="54"
          viewBox="0 0 72 69"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M32.4521 3V8.52226M53.278 11.6267L49.373 15.5316M15.5312 49.373L11.6263 53.278M8.52226 32.4521H3M15.5312 15.5312L11.6263 11.6263M44.6511 68.4112L30.4405 31.6307C30.1526 30.8856 30.8856 30.1526 31.6307 30.4405L68.4112 44.6511C69.1294 44.9283 69.2097 45.9124 68.5463 46.303L54.7469 54.42C54.6118 54.4995 54.4995 54.6118 54.42 54.7469L46.303 68.5463C45.9124 69.2097 44.9283 69.1294 44.6511 68.4112Z"
            stroke="#A5A9AD"
            strokeWidth="1.66667"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>
    </div>
  );
};
